import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import { AdminQueueState } from './ui/AdminQueueState';
import { useToast } from '../contexts/ToastContext';
import {
  adminGetPendingPerformanceRecords,
  adminVerifyPerformanceRecord,
  adminInvalidatePerformanceRecord,
} from '../services/api/adminExpansion';
import type { AdminPendingPerformanceRecord } from '../services/api/adminExpansion';

type PendingConfirm =
  | { kind: 'verify'; record: AdminPendingPerformanceRecord }
  | { kind: 'invalidate'; record: AdminPendingPerformanceRecord };

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

/** Admin queue: verify or invalidate owner-reported performance records. */
export const AdminPerformanceModeration: React.FC = () => {
  const { showToast } = useToast();
  const [records, setRecords] = useState<AdminPendingPerformanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRecords(await adminGetPendingPerformanceRecords());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load performance records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmAction = async () => {
    if (!pendingConfirm) return;
    setBusy(true);
    try {
      if (pendingConfirm.kind === 'verify') {
        await adminVerifyPerformanceRecord(pendingConfirm.record.id, 'admin_verified');
        showToast('Record verified.', 'success');
      } else {
        await adminInvalidatePerformanceRecord(pendingConfirm.record.id);
        showToast('Record invalidated and archived.', 'success');
      }
      setPendingConfirm(null);
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Action failed', 'error');
      setPendingConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        eyebrow="Admin"
        title="Performance records"
        description="Verify or invalidate owner-reported dyno and performance claims on builds."
        actions={
          <Button type="button" variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      {error ? (
        <AdminQueueState
          variant="error"
          title="Could not load performance queue"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}

      {loading ? (
        <p className="text-gray-400 text-sm" role="status">
          Loading…
        </p>
      ) : !error && records.length === 0 ? (
        <AdminQueueState
          variant="empty"
          title="Performance queue is clear"
          description="Owner-reported and evidence-attached records awaiting review will appear here."
        />
      ) : !loading && records.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
          <table className="min-w-full text-sm">
            <caption className="sr-only">Pending performance records</caption>
            <thead className="bg-[#141414]/80 text-gray-400 text-left">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Vehicle
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Record
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Value
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Date
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {records.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">
                    <p className="text-white">{r.vehicle_label}</p>
                    {r.owner_username ? (
                      <p className="text-gray-500 text-xs">@{r.owner_username}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {r.record_type_label}
                    {r.title ? <p className="text-gray-500 text-xs">{r.title}</p> : null}
                    {r.shop_name ? <p className="text-gray-500 text-xs">{r.shop_name}</p> : null}
                  </td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                    {r.display_value || '—'}
                  </td>
                  <td className="px-4 py-3 text-amber-300 text-xs">
                    {r.verification_status.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {formatDate(r.date_recorded)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setPendingConfirm({ kind: 'verify', record: r })}
                      >
                        Verify
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => setPendingConfirm({ kind: 'invalidate', record: r })}
                      >
                        Invalidate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(pendingConfirm)}
        title={
          pendingConfirm?.kind === 'verify'
            ? 'Verify performance record'
            : pendingConfirm?.kind === 'invalidate'
              ? 'Invalidate performance record'
              : ''
        }
        description={
          pendingConfirm?.kind === 'verify'
            ? `Mark "${pendingConfirm.record.record_type_label}" for ${pendingConfirm.record.vehicle_label} as admin verified? Verified records count toward achievements.`
            : pendingConfirm?.kind === 'invalidate'
              ? `Invalidate and archive "${pendingConfirm.record.record_type_label}" for ${pendingConfirm.record.vehicle_label}? The record will no longer appear on the build page.`
              : ''
        }
        confirmLabel={pendingConfirm?.kind === 'invalidate' ? 'Invalidate' : 'Verify record'}
        destructive={pendingConfirm?.kind === 'invalidate'}
        loading={busy}
        loadingLabel="Working…"
        onConfirm={() => void confirmAction()}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  );
};

export default AdminPerformanceModeration;
